import { useEffect, useState } from "react";
import * as THREE from "three";
import { focusRingDiameterFrac } from "../../../lib/ptzPanoramaMap.ts";
import { projectPatrolFocusRingToHost } from "./patrolPanoramaFocusRing.js";

/**
 * Focus ring on patrol immersive 360° after click-to-aim (matches wankhede-360).
 */
export function PatrolPanoramaFocusRing({
  focusNorm,
  enabled,
  canvasRef,
  cameraRef,
  hostRef,
  viewerRotation,
  ptzZoom = 5,
  ptzZoomMin = 1,
  ptzZoomMax = 25,
}) {
  const [ringStyle, setRingStyle] = useState({ display: "none" });

  useEffect(() => {
    if (!enabled || !focusNorm) {
      setRingStyle({ display: "none" });
      return undefined;
    }

    let raf = 0;
    const tick = () => {
      const canvas = canvasRef.current;
      const camera = cameraRef.current;
      const host = hostRef.current;
      if (!canvas || !camera || !host) {
        raf = requestAnimationFrame(tick);
        return;
      }

      camera.rotation.order = "YXZ";
      camera.rotation.y = THREE.MathUtils.degToRad(viewerRotation.yaw);
      camera.rotation.x = THREE.MathUtils.degToRad(viewerRotation.pitch);
      camera.fov = viewerRotation.fov;
      camera.updateProjectionMatrix();
      camera.updateMatrixWorld(true);

      const proj = projectPatrolFocusRingToHost(
        canvas,
        camera,
        host,
        focusNorm.normX,
        focusNorm.normY,
      );

      if (!proj.visible) {
        setRingStyle({ display: "none" });
      } else {
        const diameterPx =
          focusRingDiameterFrac(ptzZoom, ptzZoomMin, ptzZoomMax) *
          proj.hostWidth;
        setRingStyle({
          display: "block",
          position: "absolute",
          left: `${proj.left}px`,
          top: `${proj.top}px`,
          width: `${diameterPx}px`,
          height: `${diameterPx}px`,
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
          zIndex: 8,
        });
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [
    enabled,
    focusNorm,
    canvasRef,
    cameraRef,
    hostRef,
    viewerRotation.yaw,
    viewerRotation.pitch,
    viewerRotation.fov,
    ptzZoom,
    ptzZoomMin,
    ptzZoomMax,
  ]);

  if (!enabled || !focusNorm) return null;

  return (
    <div
      className="ds-preview-focus-ring ds-preview-focus-ring--immersive patrol-panorama-focus-ring"
      style={ringStyle}
      aria-hidden
    />
  );
}
